import React, { useState, useMemo } from 'react';
import { MapContainer, TileLayer, CircleMarker, Popup, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { useStore } from '@/store/useStore';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { MapPin, Layers, ZoomIn } from 'lucide-react';

// Fix for default marker icons in react-leaflet
import L from 'leaflet';
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Component to update zoom level state
const ZoomTracker = ({ onZoomChange }) => {
  const map = useMap();
  
  React.useEffect(() => {
    const handleZoom = () => {
      onZoomChange(map.getZoom());
    };
    map.on('zoomend', handleZoom);
    return () => map.off('zoomend', handleZoom);
  }, [map, onZoomChange]);
  
  return null;
};

// Get color based on production intensity
const getColor = (intensity) => {
  if (intensity >= 80) return '#ef4444'; // red
  if (intensity >= 60) return '#f97316'; // orange
  if (intensity >= 40) return '#eab308'; // yellow
  if (intensity >= 20) return '#22c55e'; // green
  return '#2dd4bf'; // teal
};

// Get radius based on production
const getRadius = (oil, maxOil) => {
  const normalized = oil / maxOil;
  return Math.max(8, normalized * 35);
};

const MapView = () => {
  const [zoom, setZoom] = useState(6);
  const [selectedCounty, setSelectedCounty] = useState(null);
  const { counties } = useStore();

  // Texas center coordinates
  const texasCenter = [31.5, -99.9];

  const maxOil = useMemo(() => {
    return Math.max(...(counties?.map(c => c.total_oil || 0) || [1]));
  }, [counties]);

  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num?.toFixed(0) || '0';
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg sm:text-xl lg:text-2xl font-heading font-bold tracking-wide uppercase">
            Production Heatmap
          </h1>
          <p className="text-xs sm:text-sm text-muted-foreground">
            Texas Permian Basin County Production
          </p>
        </div>
        <Badge variant="outline" className="border-primary/50 text-primary font-mono text-xs">
          <ZoomIn className="w-3 h-3 mr-1" />
          {zoom.toFixed(1)}x
        </Badge>
      </div>

      {/* Map Container */}
      <Card className="bg-surface border-white/10 overflow-hidden">
        <CardContent className="p-0">
          <div style={{ height: '500px', width: '100%' }}>
            <MapContainer 
              center={texasCenter} 
              zoom={zoom} 
              style={{ height: '100%', width: '100%', background: '#1a1a1a' }}
              scrollWheelZoom={true}
            >
              <ZoomTracker onZoomChange={setZoom} />
              
              {/* Dark theme tile layer */}
              <TileLayer
                attribution='&copy; <a href="https://carto.com/">CARTO</a>'
                url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
              />
              
              {/* County markers */}
              {counties?.map((county) => {
                const intensity = (county.total_oil / maxOil) * 100;
                const color = getColor(intensity);
                const radius = getRadius(county.total_oil, maxOil);
                
                return (
                  <CircleMarker
                    key={county.name}
                    center={[county.lat, county.lng]}
                    radius={radius}
                    pathOptions={{
                      fillColor: color,
                      fillOpacity: 0.7,
                      color: '#ffffff',
                      weight: 2,
                    }}
                    eventHandlers={{
                      click: () => setSelectedCounty(county),
                    }}
                  >
                    <Popup>
                      <div className="text-black p-1">
                        <h3 className="font-bold text-base mb-2">{county.name} County</h3>
                        <p className="text-sm"><strong>Oil:</strong> {formatNumber(county.total_oil)} BBL</p>
                        <p className="text-sm"><strong>Gas:</strong> {formatNumber(county.total_gas)} MCF</p>
                        <p className="text-sm"><strong>Wells:</strong> {county.active_wells || 0} active / {county.well_count || 0} total</p>
                        {county.oil_rank && <p className="text-sm"><strong>Oil Rank:</strong> #{county.oil_rank}</p>}
                      </div>
                    </Popup>
                  </CircleMarker>
                );
              })}
            </MapContainer>
          </div>
        </CardContent>
      </Card>

      {/* Legend */}
      <Card className="bg-surface border-white/10">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Layers className="w-4 h-4" />
            Production Intensity Legend
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full bg-teal-400" />
              <span className="text-xs text-muted-foreground">Low (0-20%)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full bg-green-500" />
              <span className="text-xs text-muted-foreground">Medium-Low (20-40%)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full bg-yellow-500" />
              <span className="text-xs text-muted-foreground">Medium (40-60%)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full bg-orange-500" />
              <span className="text-xs text-muted-foreground">High (60-80%)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full bg-red-500" />
              <span className="text-xs text-muted-foreground">Very High (80-100%)</span>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-3">
            <MapPin className="w-3 h-3 inline mr-1" />
            Click on a county circle for detailed production data. Data source: Texas RRC October 2025.
          </p>
        </CardContent>
      </Card>

      {/* Selected County Details */}
      {selectedCounty && (
        <Card className="bg-surface border-primary/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">{selectedCounty.name} County Details</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div>
                <p className="text-xs text-muted-foreground">Oil Production</p>
                <p className="text-lg font-bold text-primary">{formatNumber(selectedCounty.total_oil)} BBL</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Gas Production</p>
                <p className="text-lg font-bold text-secondary">{formatNumber(selectedCounty.total_gas)} MCF</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Active Wells</p>
                <p className="text-lg font-bold">{selectedCounty.active_wells || 0}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Oil Rank</p>
                <p className="text-lg font-bold">#{selectedCounty.oil_rank || 'N/A'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default MapView;
